import { makeStyles, createStyles, IconButton, Typography, Hidden, Select, MenuItem, Button, Dialog, DialogContent, DialogActions, Divider, Checkbox, Theme } from '@material-ui/core';
import { red, grey } from '@material-ui/core/colors';
import { Delete, DeleteOutline, FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage, ExpandMore, ExpandLess } from '@material-ui/icons';
import { TemplatedText } from './TemplatedText';
import { LayoutNodeWrapper } from './LayoutNode';
import React, { useState, useContext, FC, ChangeEvent, useCallback } from 'react';
import { MobileFieldControlDefinition, MobileViewDefinition, parseFieldIdentifier, resolvePointer, DFSPrimitiveType } from '../services';
import { FormContext, useDataValue, useEditability } from '..';
import { FormControlProps } from '../utils';
import { FormControl } from './FormControl';
import { useConfirmation } from '../ConfirmContext';

export interface ActionBarProps {
  count: number;
  disableCreate: boolean;
}
interface SubformColumn {
  label?: string;
  value: string;
}
export interface SubformDefinition extends MobileFieldControlDefinition<'Subform'> {
  rowLabel: {
      columns: SubformColumn[];
      params: {
          [key: string]: any;
      };
  };
  rowBody: MobileViewDefinition;
  /**
   * Whether to disable the 'New' button
   */
  disableCreate?: boolean;
  maxItems?: number;
  beforeCreate?: {
      on: string // first or all
      prompt: {
        title: string;
        text: string;
    }
  }
}

export interface SubformProps extends FormControlProps<SubformDefinition> {};

const CREATE_BUTTON_WIDTH = '80px';
const useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    root: {
      flex: 1,
      display: 'flex',
      marginBottom: theme.spacing(2),
      maxWidth: '100%'
    },
    control: {
      display: 'flex',
      flexDirection: 'column',
      margin: theme.spacing(1, 0, 0),
      border: 'thin solid ' + theme.palette.divider,
      maxWidth: '100%'
    },
    header: {
      flex: 1,
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
      height: '36px',
      justifyContent: 'center',
      color: 'inherit',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#BDCCEB'
      }
    },
    activeRowHeader: {
      backgroundColor: '#E4EBFA'
    },
    headerRow: {
      flex: 1
    },
    headerTitle: {
      height: '100%',
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1)
    },
    headerRowIndex: {
      width: '1em'
    },
    headerRowContent: {
      flex: 1,
      display: 'flex',
      paddingLeft: theme.spacing(9)
    },
    subformRowContent: {
      flex: 1,
      display: 'flex',
      alignSelf: 'stretch',
      overflow: 'hidden'
    },
    headerColumnText: {
      flex: 1,
      color: theme.palette.text.secondary,
      textAlign: 'center',
      wordBreak: 'break-word',
      alignSelf: 'center',
      verticalAlign: 'middle'
    },
    list: {},
    row: {
      backgroundColor: '#c2c2c2'
    },
    contents: {
      margin: theme.spacing(0, 2, 0),
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      position: 'relative'
    },
    create: {
      width: CREATE_BUTTON_WIDTH,
      marginLeft: theme.spacing(2),
      alignSelf: 'stretch',
      borderRadius: 0
    },
    toolbar: {
      display: 'flex',
      flex: 1,
      padding: 0,
      alignItems: 'center'
    },
    pagingControls: {
      display: 'flex',
      justifyContent: 'center'
    },
    footerText: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1)
    },
    selectText: {
      marginRight: theme.spacing(1)
    },
    spacer: {
      flex: 1
    },
    expandIcon: {
      marginRight: theme.spacing(1)
    },
    rowSelect: {
      paddingRight: theme.spacing(4)
    },
    confirmDeleteButton: {
      color: red[800]
    },
    trashButton: {
      color: red[800]
    }
  })
});
// is this used?
// weird typings
const ActionBar: FC<any> = function(props) {
  const { onChangePage, page, count, rowsPerPage, rowsPerPageOptions, selection, onDelete, onChangeRowsPerPage, onCreate, disabled, disableCreate } = props;
  const classes = useStyles({});
  const canGoBack = page === 0;
  const canGoForward = page === Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const canDelete = Object.keys(selection).length > 0;
  return (
    <div className = { classes.toolbar }>
      <div>
        <IconButton disabled = { !canDelete || disabled } onClick = { onDelete }>
          { canDelete
            ? <Delete className = { classes.trashButton }></Delete>
            : <DeleteOutline></DeleteOutline>
          }
        </IconButton>
        <Typography variant = { 'caption' }>
          { Object.keys(selection).length + ' selected' }
        </Typography>
      </div>
      <div className = { classes.spacer }></div>
      <Hidden smDown>
        <div className = { classes.footerText }>
          <Typography variant = { 'caption' } className = { classes.selectText }>
            Rows per page
          </Typography>
          <Select
            value = { rowsPerPage }
            onChange = { (event, _) => onChangeRowsPerPage(event) }
            inputProps = { {
              name: 'rows',
              id: 'row-count'
            } }
            classes = { { select: classes.rowSelect } }>
            { rowsPerPageOptions.map((opt: number, idx: number) =>
              <MenuItem key = { idx } value = { opt }>
                { opt }
              </MenuItem>
            )}
          </Select>
        </div>
        <Typography variant = { 'caption' } className = { classes.footerText }>
          { count > 0 ? page * rowsPerPage + 1 + '-' + Math.min(page * rowsPerPage + rowsPerPage, count) + ' of ' + count : '' }
        </Typography>
      </Hidden>
      <div className = { classes.pagingControls }>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoBack }
          onClick = { (e) => onChangePage(e, 0) }>
          <FirstPage></FirstPage>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoBack }
          onClick = { (e) => onChangePage(e, page - 1) }>
          <KeyboardArrowLeft></KeyboardArrowLeft>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoForward }
          onClick = { (e) => onChangePage(e, page + 1) }>
          <KeyboardArrowRight></KeyboardArrowRight>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoForward }
          onClick = { (e) => onChangePage(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1)) }>
          <LastPage></LastPage>
        </IconButton>
      </div>
      {
        !disableCreate && (
          <Button
            className = { classes.create }
            variant = { 'contained' }
            color = { 'secondary' }
            onClick = { onCreate }
            disabled = { disabled }>
              New
          </Button>
        )
      }
    </div>
  )
};

export const Subform: FC<SubformProps> = function(props) {
  const { definition, path } = props;
  const classes = useStyles({});
  const [ page, setPage ] = useState(0);
  const [ currentSubform, setCurrentSubform ] = useState(-1);
  const [ rowsPerPage, setRowsPerPage ] = useState(5);
  const [ checked, setChecked ] = useState<{ [ key: string ]: boolean}>({});
  const [ confirmOpen, setConfirmOpen ] = useState(false);
  const formContext = useContext(FormContext);
  const { onSetData, onRemoveData, scope } = formContext;
  const dataPath = definition.dataPath;
  const beforeCreate = definition.beforeCreate;
  const maxItems = definition.maxItems;
  const disableCreate = definition.disableCreate;
  const subforms = useDataValue(definition.dataPath) as undefined | readonly DFSPrimitiveType[];
  const editable = useEditability(definition.editable);
  const relativeScope = resolvePointer(dataPath, scope);
  const confirm = useConfirmation();

  const createItem = useCallback(() => {
    onSetData(dataPath.slice(0, -1).concat('/-}'), scope, {});
    if (!subforms) {
      Promise.resolve().then(function() {
        return setCurrentSubform(0);
      })
    } else if (page * rowsPerPage + rowsPerPage > subforms!.length) {
      if (subforms) {
        Promise.resolve().then(function() {
          return setCurrentSubform(subforms!.length % rowsPerPage);
        });
      } else {
        Promise.resolve().then(function() {
          return setCurrentSubform(0);
        })
      }
    } else {
      Promise.resolve().then(function() {
        return setPage(Math.floor(subforms!.length / rowsPerPage));
      }).then(function() {
        return setCurrentSubform(subforms!.length % rowsPerPage);
      })
    }
  }, [ subforms, dataPath, page, rowsPerPage ])

  const confirmCreate = useCallback(() => {
    if (!beforeCreate) {
      return;
    }

    confirm({
      variant: "warning",
      catchOnCancel: true,
      title: beforeCreate!.prompt.title,
      text: beforeCreate!.prompt.text
    })
      .then(() => {
        createItem();
      })
      .catch((e) => {
        console.warn('Unable to invoke beforeCreate:', e);
      });
  }, [ beforeCreate, confirm, createItem ])

  const handleCreate = useCallback(() => {
    if (beforeCreate) {
      if (beforeCreate.on === 'first') {
        if (subforms?.length === 0) {
          confirmCreate();
        } else {
          createItem();
        }
      } else {
        confirmCreate();
      }
    } else {
      createItem();
    }
  }, [createItem, confirmCreate, beforeCreate]);

  const handleSelectRow = function(checked: { [key: string]: boolean }, idx: number, setChecked: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>) {
    return function(event: Event) {
      // let l;
      let newSelection;
      if (checked[idx]) {
        newSelection = Object.assign({}, checked);
        delete newSelection[idx]
      } else {
        newSelection = {
          ...checked,
          [idx]: true
        }
        // newSelection = Object.assign(
        //   Object.assign({}, checked),
        //   { [idx]: true }
        //   // (l = {}, l[idx] = true, l)
        // )
      }
      setChecked(newSelection);
      event.stopPropagation();
    }
  };

  const handleDeleteSelectedRows = function() {
    setConfirmOpen(true)
  };

  const performDelete = function() {
    const toDelete = Object.keys(checked).map(function(str) {
      return parseInt(str);
    }).sort();
    toDelete.reverse().forEach(function(row) {
      const adjustedRow = page * rowsPerPage + row;
      console.log('deleting:', '{' + parseFieldIdentifier(definition.dataPath) + '/' + adjustedRow + '}', scope);
      onRemoveData('{' + parseFieldIdentifier(definition.dataPath) + '/' + adjustedRow + '}', scope);
    });
    if (currentSubform >= 0 && !toDelete.includes(currentSubform)) {
      const t = toDelete.filter(function(toDelete) {
        return toDelete < currentSubform
      }).length;
      setCurrentSubform(currentSubform - t);
    } else if (toDelete.includes(currentSubform)) {
      setCurrentSubform(-1)
    }
    setChecked({});
    setConfirmOpen(false);
  };
  const handleChangeRowsPerPage = function(event: any) {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    setCurrentSubform(0);
  };
  const handleChangePage = function(_: ChangeEvent, newPage: number) {
    setPage(newPage);
    setCurrentSubform(-1);
  };
  const handleCloseConfirm = function() {
    return setConfirmOpen(false);
  };
  return (
    <div className = { classes.root }>
      <FormControl
        label = { definition.label }
        style = { definition.style }
        helpText = { definition.helpText }>
        <Dialog
          open = { confirmOpen }
          onClose = { handleCloseConfirm }
          aria-describedby = { 'confirm-dialog-description' }>
          <DialogContent id = { 'confirm-dialog-description' }>
            Are you sure you want to delete { Object.keys(checked).length } entries?
          </DialogContent>
          <DialogActions>
            <Button onClick = { handleCloseConfirm } color = { 'primary' } autoFocus>
              Cancel
            </Button>
            <Button onClick = { performDelete }>
              <span className = { classes.confirmDeleteButton }>
                Delete
              </span>
            </Button>
          </DialogActions>
        </Dialog>
        <div className = { classes.control }>
          <div className = { classes.headerRow }>
            <div className = { classes.headerRowContent }>
              { definition.rowLabel.columns.map((col, i: number) =>
                <div className = { classes.headerColumnText } key = { i }>
                  <Typography variant = { 'subtitle2' }>
                    { col.label }
                  </Typography>
                </div>
              )}
            </div>
            <Divider variant = { 'fullWidth' }></Divider>
          </div>
          <div className = { classes.list }>
            { subforms?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(function(_, idx) {
              const isExpanded = currentSubform === idx;
              return (
                <div className = { classes.row } key = { idx }>
                  <FormContext.Provider
                    value = { Object.assign(
                      Object.assign({}, formContext), {
                        scope: parseFieldIdentifier(relativeScope) + '/' + (page * rowsPerPage + idx)
                      }
                    )}>
                    <div
                      className = { [classes.header, isExpanded ? classes.activeRowHeader : ''].join(' ') }
                      onClick = { () => setCurrentSubform(idx === currentSubform ? -1 : idx) }>
                        {/* typings */}
                      <Checkbox checked = { checked[idx] === true } onClick = { handleSelectRow(checked, idx, setChecked) as any }></Checkbox>
                      <div className = { classes.headerTitle }>
                        <Typography variant = { 'caption' } className = { classes.headerRowIndex }>
                          { page * rowsPerPage + idx + 1 }.
                        </Typography>
                        <div className = { classes.subformRowContent }>
                          { definition.rowLabel.columns.map((e, r) =>
                            <div className = { classes.headerColumnText } key = { r }>
                              <TemplatedText template = { e.value } params = { definition.rowLabel.params }></TemplatedText>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className = { classes.expandIcon }>
                        { isExpanded
                          ? <ExpandMore></ExpandMore>
                          : <ExpandLess></ExpandLess>
                        }
                      </div>
                    </div>
                    <Divider variant = { 'fullWidth' }></Divider>
                    { isExpanded &&
                      <React.Fragment>
                        <div
                          className = { classes.contents }
                          style = { {
                            pointerEvents: editable ? 'auto' : 'none',
                            backgroundColor: editable ? undefined : grey[100]
                          } }>
                          <LayoutNodeWrapper path = { path.concat('/contents') }></LayoutNodeWrapper>
                        </div>
                      </React.Fragment>
                    }
                    <Divider variant = { 'fullWidth' }></Divider>
                  </FormContext.Provider>
                </div>
              )
            })}
          </div>
        </div>
        <div className = { classes.footer }>
          <ActionBar
            disableCreate = { disableCreate || (typeof maxItems === 'number' && subforms && maxItems <= subforms!.length) }
            count = { subforms !== undefined ? subforms!.length : 0 }
            rowsPerPage = { rowsPerPage }
            page = { page }
            selection = { checked }
            onDelete = { handleDeleteSelectedRows }
            onChangePage = { handleChangePage }
            rowsPerPageOptions = { [5, 10, 15, 25, 50] }
            onChangeRowsPerPage = { handleChangeRowsPerPage }
            onCreate = { handleCreate }
            disabled = { !editable }>
          </ActionBar>
        </div>
      </FormControl>
    </div>
  )
};
